'use client'

import { useState } from 'react'
import { motion } from 'motion/react'
import { Icon } from '@iconify/react'
import { cn } from '@/lib/utils'

// Iconify icon configurations with unique hover animations
const iconConfig = {
  strategy: {
    icon: 'solar:compass-big-bold-duotone',
    hoverIcon: 'solar:compass-big-bold',
    gradient: ['#10b981', '#06b6d4'],
    animation: {
      rotate: [0, 360],
      scale: [1, 1.15, 1],
    },
    transition: { duration: 0.8, ease: 'easeInOut' },
  },
  design: {
    icon: 'solar:layers-bold-duotone',
    hoverIcon: 'solar:layers-bold',
    gradient: ['#8b5cf6', '#d946ef'],
    animation: {
      rotateY: [0, 180, 360],
      scale: [1, 1.1, 1],
    },
    transition: { duration: 0.6, ease: 'easeInOut' },
  },
  code: {
    icon: 'solar:code-square-bold-duotone',
    hoverIcon: 'solar:code-square-bold',
    gradient: ['#06b6d4', '#3b82f6'],
    animation: {
      x: [-2, 2, -2, 2, 0],
      scale: [1, 1.1, 1],
    },
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
  ecommerce: {
    icon: 'solar:bag-heart-bold-duotone',
    hoverIcon: 'solar:bag-heart-bold',
    gradient: ['#f59e0b', '#ef4444'],
    animation: {
      y: [0, -8, 0],
      scale: [1, 1.15, 1],
    },
    transition: { duration: 0.5, type: 'spring', stiffness: 400 },
  },
  seo: {
    icon: 'solar:chart-2-bold-duotone',
    hoverIcon: 'solar:chart-2-bold',
    gradient: ['#ec4899', '#f43f5e'],
    animation: {
      scaleY: [1, 1.2, 1],
      y: [0, -3, 0],
    },
    transition: { duration: 0.4, ease: 'easeOut' },
  },
  ai: {
    icon: 'solar:cpu-bolt-bold-duotone',
    hoverIcon: 'solar:cpu-bolt-bold',
    gradient: ['#a855f7', '#6366f1'],
    animation: {
      scale: [1, 1.2, 1],
      rotate: [0, 10, -10, 0],
    },
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
}

const solutionMapping = {
  'Product Strategy': 'strategy',
  'Design Systems & UX': 'design',
  'Full‑Stack Development': 'code',
  'E‑commerce & Subscriptions': 'ecommerce',
  'SEO & Performance': 'seo',
  'AI‑powered Workflows': 'ai',
}

// Animated Icon wrapper component
function AnimatedIcon({ iconKey, isHovered, size = 28 }) {
  const config = iconConfig[iconKey]
  if (!config) return null

  const defaultState = { scale: 1, scaleY: 1, rotate: 0, rotateY: 0, x: 0, y: 0 }

  return (
    <div className="relative">
      {/* Glow effect behind icon */}
      <motion.div
        className="absolute inset-0 rounded-full blur-lg"
        style={{
          background: `linear-gradient(135deg, ${config.gradient[0]}40, ${config.gradient[1]}40)`,
        }}
        initial={{ opacity: 0, scale: 1 }}
        animate={{
          opacity: isHovered ? 0.8 : 0,
          scale: isHovered ? 1.5 : 1,
        }}
        transition={{ duration: 0.2 }}
      />
      
      {/* Main icon */}
      <motion.div
        initial={defaultState}
        animate={isHovered ? config.animation : defaultState}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{ 
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Icon
          icon={isHovered ? config.hoverIcon : config.icon}
          width={size}
          height={size}
          style={{
            color: config.gradient[0],
            filter: isHovered ? `drop-shadow(0 0 8px ${config.gradient[0]}80)` : 'none',
            transition: 'filter 0.2s ease',
          }}
        />
      </motion.div>
    </div>
  )
}

export function SolutionCard({ title, children, className }) {
  const [isHovered, setIsHovered] = useState(false)
  const iconKey = solutionMapping[title] || 'strategy'
  const config = iconConfig[iconKey]
  const gradientColors = config?.gradient || ['#10b981', '#06b6d4']

  return (
    <motion.div
      className={cn(
        'group relative rounded-2xl overflow-hidden',
        'bg-gradient-to-br from-neutral-900 to-neutral-950',
        'border border-neutral-800/50',
        'transition-all duration-500',
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      {/* Gradient border glow on hover */}
      <motion.div
        className="absolute -inset-[1px] rounded-2xl opacity-0 blur-sm"
        style={{
          background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
        }}
        animate={{ opacity: isHovered ? 0.6 : 0 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Inner card content */}
      <div className="relative p-6 lg:p-8 h-full bg-gradient-to-br from-neutral-900/95 to-neutral-950/95 rounded-2xl">
        {/* Top accent line */}
        <motion.div
          className="absolute top-0 left-6 right-6 h-[2px] rounded-full"
          style={{
            background: `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})`,
          }}
          animate={{ 
            scaleX: isHovered ? 1 : 0,
            opacity: isHovered ? 1 : 0
          }}
          transition={{ duration: 0.4 }}
          initial={{ originX: 0 }}
        />

        {/* Icon Container */}
        <div className="relative mb-6">
          <motion.div
            className="w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-neutral-800/80 to-neutral-900/80 border border-neutral-700/50"
            animate={{
              boxShadow: isHovered 
                ? `0 0 30px 5px ${gradientColors[0]}30`
                : '0 0 0 0 transparent',
              borderColor: isHovered ? `${gradientColors[0]}50` : 'rgb(64 64 64 / 0.5)',
            }}
            transition={{ duration: 0.4 }}
          >
            <AnimatedIcon iconKey={iconKey} isHovered={isHovered} size={28} />
          </motion.div>
        </div>

        {/* Content */}
        <div className="space-y-3">
          <motion.h3 
            className="text-xl font-semibold text-white tracking-tight"
            animate={{ x: isHovered ? 4 : 0 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            {title}
          </motion.h3>
          <p className="text-neutral-300 text-sm leading-relaxed">
            {children}
          </p>
        </div>

        {/* Bottom gradient fade */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none rounded-b-2xl"
          style={{
            background: `linear-gradient(to top, ${gradientColors[0]}15, transparent)`,
          }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.4 }}
        />
        
        {/* Corner accent */}
        <motion.div
          className="absolute bottom-4 right-4"
          animate={{ 
            opacity: isHovered ? 1 : 0,
            scale: isHovered ? 1 : 0.8,
          }}
          transition={{ duration: 0.3 }}
        >
          <Icon 
            icon="solar:arrow-right-up-linear" 
            width={20} 
            height={20}
            style={{ color: gradientColors[0] }}
          />
        </motion.div>
      </div>
    </motion.div>
  )
}

export function SolutionGrid({ children, className }) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3',
        className
      )}
    >
      {children}
    </div>
  )
}
